import React, { useContext, useMemo } from 'react';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { AppContext } from '../../../../providers/AppProvider';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { Link } from 'react-router-dom';
import { RadioButtons } from '../../../../components/forms/RadioButtons';
import { RadioButtonsType } from '../../../../components/forms/RadioButtons/RadioButtons.view';
import { PostEditorContextEnum } from '../../../../models/post-editor';
import {
  GetPostsEditorDocument,
  GetPostsEditorQuery,
  GetPostsEditorQueryVariables,
  PostQueuePosition,
  PostStatus,
  PostTiming
} from '../../../../models/graphql';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { FormElementAction, FormElementValueType } from '../../../../models/basic-objects';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { useApolloClient } from '@apollo/client';
import { getCustomizationProfileId } from '../../validators/helpers/customization-getter';
import { hasSeverity } from '../../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { getErrorMessages } from '../../validators/helpers/getErrorMessages';
import { formatErrorMessages } from '../../validators/helpers/formatErrorMessages';
import { Tooltip } from '../../../../components/actions/Tooltip';
import classNames from 'classnames';
import classes from '../SchedulePost/SchedulePost.module.scss';
import { BeaconContext } from 'providers/BeaconProvider';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';

export type AddToQueueViewProps = {
  //
  formInstance: UseFormReturn;
  fetchedPostId?: string;
};

export const AddToQueueView = ({ formInstance, fetchedPostId }: AddToQueueViewProps): JSX.Element => {
  const { cache } = useApolloClient();
  const {
    postEditorContext: { type: postEditorContextType }
  } = useContext(AppContext);
  const { beacon } = useContext(BeaconContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { state, dispatch } = useContext(PostEditorContext);
  const postGroup = state.postGroups[postGroupIndex];
  const { groupPostAttributes, posts, readOnlyPosts, errors } = postGroup;

  const queuePosition =
    typeof customizeIndex === 'number' && postGroup.customizeSchedulePerProfile
      ? posts[customizeIndex].scheduling.queuePosition
      : groupPostAttributes.scheduling.queuePosition;

  const queuePositionName =
    typeof customizeIndex === 'number' && postGroup.customizeSchedulePerProfile
      ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.queuePosition`
      : `postGroup.${postGroupIndex}.queuePosition`;
  const postStatus =
    typeof customizeIndex === 'number' && postGroup.customizeSchedulePerProfile
      ? posts[customizeIndex].postStatus
      : groupPostAttributes.postStatus;

  const postId =
    typeof customizeIndex === 'number' && postGroup.customizeSchedulePerProfile ? posts[customizeIndex].postId : null; // we can only have postID in customize mode

  const reactivated =
    typeof customizeIndex === 'number' && postGroup.customizeSchedulePerProfile
      ? readOnlyPosts.map(p => p.customizedProfileId).includes(posts[customizeIndex].customizedProfileId)
      : false;

  const customizationProfileId = getCustomizationProfileId(postGroup, customizeIndex);

  const hasError = hasSeverity(errors, ErrorMessageSeverity.error, customizationProfileId, null, [
    'checkQueuePosition',
    'pinterestDuplicatesCheck',
    'preventTwDuplicate'
  ]);

  const stringifiedErrors = JSON.stringify(
    hasError
      ? getErrorMessages(errors, customizationProfileId, null, [
          'checkQueuePosition',
          'pinterestDuplicatesCheck',
          'preventTwDuplicate'
        ])
      : []
  );

  const memoizedErrors = useMemo(() => {
    const messages: string[] = JSON.parse(stringifiedErrors);
    return messages;
  }, [stringifiedErrors]);

  const formattedErrors = useMemo(() => formatErrorMessages(memoizedErrors), [memoizedErrors]);

  let hasCurrentQueuePosition = false;

  if (fetchedPostId && postId) {
    const cachedFamily = cache.readQuery<GetPostsEditorQuery, GetPostsEditorQueryVariables>({
      query: GetPostsEditorDocument,
      variables: {
        postId: fetchedPostId
      }
    });
    const fetchedPost = cachedFamily?.latestFamilyMembersOfPost.find(post => post.id === postId);
    if (fetchedPost) {
      hasCurrentQueuePosition = fetchedPost.timing === PostTiming.QUEUE && fetchedPost.status === PostStatus.ACTIVE;
    }
  }

  const radioItems = useMemo((): RadioButtonsType[] => {
    const items: RadioButtonsType[] = [
      {
        label: (
          <>
            Top of Queue
            {!!memoizedErrors.length && (
              <Tooltip
                className={classNames(classes.Tooltip, classes.TooltipRadio)}
                icon={'alert'}
                target={`#queueTopDuplicatesTooltipError`}
                position={'right'}
                pointer={true}
                type={'inlined'}
                autoZIndex={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}>
                {formattedErrors}
              </Tooltip>
            )}
          </>
        ),
        value: PostQueuePosition.TOP
      },
      {
        label: 'Bottom of Queue',
        value: PostQueuePosition.BOTTOM
      }
    ];

    if (
      postEditorContextType === PostEditorContextEnum.edit &&
      postStatus === PostStatus.ACTIVE &&
      postId &&
      !reactivated &&
      hasCurrentQueuePosition
    ) {
      items.splice(1, 0, {
        label: 'Current Queue Position',
        value: 'CURRENT'
      });
    }
    return items;
  }, [
    postEditorContextType,
    postStatus,
    postId,
    reactivated,
    hasCurrentQueuePosition,
    formattedErrors,
    memoizedErrors
  ]);

  const radioRules: RegisterOptions = {};

  const isPostQueuePositionGuard = (value: FormElementValueType): value is PostQueuePosition => {
    if (typeof value !== 'string') throw new Error('value is not of type string!');
    return value in PostQueuePosition || value === 'CURRENT';
  };
  const updateAction: FormElementAction = (value, complete) => {
    if (!isPostQueuePositionGuard(value)) throw new Error('value is not a proper enum');
    complete();
    dispatch({
      type: 'setQueuePosition',
      payload: {
        queuePosition: value,
        postGroupIndex,
        customizeIndex
      }
    });
  };

  return (
    <div className={'margin-top-small-16'}>
      <RadioButtons
        action={updateAction}
        value={queuePosition ?? undefined}
        wrapper={true}
        displayType={'block'}
        formInstance={formInstance}
        items={radioItems}
        name={queuePositionName}
        rules={radioRules}
        size={'medium'}
      />
      <BoxNotification type={'info'}>
        <p className={'no-b-m'}>
          Automatically schedule your Post based on the weekly timeslots in your{' '}
          <Link to={'/publish/plan'} target={'_blank'}>
            Posting Plan
          </Link>
          . Choosing &apos;Top of Queue&apos; publishes it at the next available timeslot, while &apos;Bottom of
          Queue&apos; places it after existing scheduled content.
        </p>
        <a onClick={() => beacon('article', '693', { type: 'modal' })}>Learn more</a>.
      </BoxNotification>
    </div>
  );
};
